// Права в интерфейсе (T034; FR-038, FR-038а, FR-016): пункты меню и «Создать ▾» — только то,
// что разрешено ролям пользователя; прямой переход в закрытый раздел — экран «Нет доступа»
// с причиной, ФИО администраторов и кодом. Решает всегда сервер: интерфейс только не
// показывает лишнего, а при отказе спрашивает сервер — тот отвечает кодом и пишет отказ
// в журнал (FR-012, FR-016а).
//
// 030 (T019, T020; FR-031, FR-033, FR-034; ASG-DESK-07, ASG-DESK-08, ASG-DESK-15): «Мои задачи» —
// в личной группе после «Главной», по связи учётной записи с человеком (me.person), а не по
// праву: items.own есть у каждого в сеансе. Сеанс по ссылке — узкое меню: только «Мои задачи»
// (и «Помощь» — действием, Sidebar.tsx). Сеансу по ссылке и пользователю без ролей «Главная» —
// это «Мои задачи» (homeIsMyItems): пункта «Главная» в меню у них нет, адрес «/» ведёт в «/my».
//
// 032 (T026; FR-002, FR-003; ASG-HIST-03, ASG-HIST-04): «Входящие» — в личной группе сразу после
// «Главной», у всех с учётной записью и в узком меню сеанса по ссылке. У кого «Главная» — это «Мои
// задачи», «Входящие» — сразу после них. Счётчик «Входящих» — непрочитанные «требует действия»
// (actionUnread ленты уведомлений), единственный акцентный в меню; «Оповещения» — серые.
//
// 032 (T028; FR-021; ASG-FLOW-04): «Просрочено у ваших сотрудников» — в личной группе после «Моих
// задач», по признаку руководителя me.team, а не по праву (items.team есть у каждого в сеансе);
// и в узком меню сеанса по ссылке: туда ведёт кнопка «Открыть» письма руководителю.
import { renderError } from '@kp/contracts';
import type { ErrorCode, MeResponse, Operation, Permissions } from '@kp/contracts';
import {
  BellRing,
  BookUser,
  ClipboardList,
  DatabaseBackup,
  FilePlus,
  FileUp,
  Files,
  House,
  Inbox,
  ListChecks,
  ListTodo,
  Logs,
  Repeat2,
  UserPlus,
  UsersRound,
} from '@kp/ui/icons';
import type { LucideIcon } from '@kp/ui';
import { redirect } from '@tanstack/react-router';
import type { ParsedLocation } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { api, isApiError } from '../api/client.ts';
import { meQuery, meQueryKey } from './session.ts';

/** Хотя бы одна операция разрешена; пустой список — доступно в сеансе. */
export function can(permissions: Permissions, operations: readonly Operation[]): boolean {
  return operations.length === 0 || operations.some((op) => permissions[op] !== undefined);
}

// --- Разделы ---

/**
 * Разделы с правом на вход и запросом-пробой: если по данным сеанса права нет, интерфейс
 * спрашивает сервер тем же запросом, что сделала бы страница.
 */
export const sections = {
  // 003: документы и таблица открытых пунктов.
  documents: {
    operations: ['documents.read'],
    probe: () => api('documents.list'),
  },
  control: {
    operations: ['control.read'],
    // Проба без пунктов: «чёрных» (закрытых) в таблице открытых пунктов не бывает.
    probe: () => api('control.items', { query: { status: 'black' } }),
  },
  // 022 (research Р3, Р11): таблица протокола — по minutes.read, а не по «Документам»: внешнему
  // роль в протоколе даёт minutes.read без documents.read. Проба — свои протоколы (minutes.mine,
  // «в сеансе»): какой протокол открыт, решает ответ minutes.state — «Нет доступа к протоколу»
  // с кодом сервера на странице протокола.
  protocol: {
    operations: ['minutes.read'],
    probe: () => api('minutes.mine'),
  },
  // 005: справочник (экран — дорожка W1, маршрут /directory), ключи агента и настройки установки.
  directory: {
    operations: ['directory.read'],
    probe: () => api('directory.records.list', { query: { limit: 1 } }),
  },
  agentKeys: {
    operations: ['agent_keys.manage'],
    probe: () => api('agentKeys.list'),
  },
  settings: {
    operations: ['settings.write'],
    probe: () => api('settings.list'),
  },
  // 008: «Почта» — настройки установки 005 (settings.write) и блок «Отправка писем» (mail.read):
  // сопровождению без settings.write раздел нужен ради подключения почты (contracts/api.md 008,
  // «Матрица прав»). Проба — подключение почты: его читают обе роли раздела.
  mail: {
    operations: ['settings.write', 'mail.read'],
    probe: () => api('delivery.connection.get'),
  },
  letters: {
    operations: ['mail.read'],
    probe: () => api('delivery.messages.list', { query: { limit: 1 } }),
  },
  // 033: «Рассылка» — режим и адреса песочницы (settings.write) и стоп-кран (mail.stop): контролёру
  // без settings.write раздел нужен ради «Остановить все сообщения». Проба — состояние стоп-крана.
  mailing: {
    operations: ['settings.write', 'mail.stop'],
    probe: () => api('delivery.safety.get'),
  },
  // 010: «Протоколы» — схемы шаблонов (templates.read); мастер подключения — templates.write
  // (contracts/api.md 010, «Матрица прав»). Проба мастера — тот же список: изменяющего запроса
  // без файла нет, а отказ мастеру сервер всё равно даст в каждом методе (FR-002).
  protocols: {
    operations: ['templates.read'],
    probe: () => api('templates.list'),
  },
  protocolOnboarding: {
    operations: ['templates.write'],
    probe: () => api('templates.list'),
  },
  users: {
    operations: ['users.read'],
    probe: () => api('users.list', { query: { limit: 1 } }),
  },
  rights: {
    operations: ['rights.read'],
    probe: () => api('rights.get'),
  },
  substitutions: {
    operations: ['substitutions.read'],
    probe: () => api('substitutions.list', { query: { limit: 1 } }),
  },
  support: {
    operations: ['support.read'],
    probe: () => api('supportAccess.list', { query: { limit: 1 } }),
  },
  audit: {
    operations: ['audit.read.content', 'audit.read.access'],
    probe: () => api('audit.list', { query: { limit: 1 } }),
  },
  deployments: {
    operations: ['ops.read'],
    probe: () => api('ops.deployments'),
  },
  // «Мои выгрузки» (002): своих заказов выгрузки журнала; в меню нет — вход из журнала,
  // тоста заказа и уведомления.
  exports: {
    operations: ['audit.export'],
    probe: () => api('audit.exports.list', { query: { limit: 1 } }),
  },
  // 002: экраны сопровождения. «Состояние» — полное (ops.status.read) или краткое
  // (ops.health.read, у администратора клиента).
  status: {
    operations: ['ops.status.read', 'ops.health.read'],
    probe: () => api('ops.health'),
  },
  jobs: {
    operations: ['ops.jobs.read'],
    probe: () => api('ops.jobs.list', { query: { limit: 1 } }),
  },
  log: {
    operations: ['ops.log.read'],
    probe: () => api('ops.log', { query: { limit: 1 } }),
  },
  alerts: {
    operations: ['ops.alerts.read'],
    probe: () => api('ops.alerts', { query: { limit: 1 } }),
  },
  backups: {
    operations: ['ops.backups.read'],
    probe: () => api('ops.backups', { query: { limit: 1 } }),
  },
} as const satisfies Record<
  string,
  { operations: readonly Operation[]; probe: () => Promise<unknown> }
>;

export type SectionId = keyof typeof sections;

// --- Меню ---

/** personal — без заголовка; support — группа служебных экранов «Сопровождение» (002, 0.22.2). */
export type NavGroup = 'personal' | 'support';

/** «Мои задачи» (030): экран дорожки F; адрес меню, «Главной» и ссылок «Моих задач». */
export const MY_ITEMS_PATH: string = '/my';

/** «Входящие» (032): адрес меню, колокольчика, нижней навигации и палитры. */
export const INBOX_PATH = '/inbox';

/** «Просрочено у ваших сотрудников» (032): страница руководителя. */
export const TEAM_PATH: string = '/team';

export interface NavItem {
  id: 'home' | 'inbox' | 'my' | 'team' | SectionId;
  label: string;
  to: string;
  icon: LucideIcon;
  /** personal — без заголовка; support — служебные экраны. */
  group: NavGroup;
  operations: readonly Operation[];
}

export const navItems: readonly NavItem[] = [
  { id: 'home', label: 'Главная', to: '/', icon: House, group: 'personal', operations: [] },
  // 032 (FR-002): сразу после «Главной»; свои уведомления есть у каждого в сеансе.
  {
    id: 'inbox',
    label: 'Входящие',
    to: INBOX_PATH,
    icon: Inbox,
    group: 'personal',
    operations: [],
  },
  // 030 (FR-033; ASG-DESK-08): после «Главной»; виден по me.person (navSession), а не по праву.
  {
    id: 'my',
    label: 'Мои задачи',
    to: MY_ITEMS_PATH,
    icon: ListTodo,
    group: 'personal',
    operations: ['items.own'],
  },
  // 032 (FR-021): после «Моих задач»; виден руководителю по me.team (navSession).
  {
    id: 'team',
    label: 'Просрочено у ваших сотрудников',
    to: TEAM_PATH,
    icon: UsersRound,
    group: 'personal',
    operations: ['items.team'],
  },
  // 003: рабочие разделы — без заголовка группы, сразу после «Главной».
  {
    id: 'documents',
    label: 'Документы',
    to: '/documents',
    icon: Files,
    group: 'personal',
    operations: sections.documents.operations,
  },
  {
    id: 'control',
    label: 'Контроль',
    to: '/control',
    icon: ClipboardList,
    group: 'personal',
    operations: sections.control.operations,
  },
  // 005: «Справочник» — после «Контроля» (макет 5); счётчик — «К разбору» (navCounters).
  {
    id: 'directory',
    label: 'Справочник',
    to: '/directory',
    icon: BookUser,
    group: 'personal',
    operations: sections.directory.operations,
  },
  // 007: «Пользователи», «Роли и права», «Замещения», «Доступ сопровождения», «Ключи агента»,
  // «Журнал действий» и настройки установки — разделы «Настроек» (settings-sections.ts). Группы
  // «Администрирование» в меню нет: пункт «Настройки» — внизу меню (ux.md «Табеля», раздел 11).
  // Группа «Сопровождение» (contracts/ui.md, «Меню»): «Фоновые задачи», «Технический журнал»,
  // «Оповещения», «Копии». «Состояние» и «Выкатки» с 0.22.2 — раздел «Сервис» «Настроек»
  // (решение владельца 31.10.2026): у администратора клиента группы в меню больше нет.
  {
    id: 'jobs',
    label: 'Фоновые задачи',
    to: '/ops/jobs',
    icon: ListChecks,
    group: 'support',
    operations: sections.jobs.operations,
  },
  {
    id: 'log',
    label: 'Технический журнал',
    to: '/ops/log',
    icon: Logs,
    group: 'support',
    operations: sections.log.operations,
  },
  {
    id: 'alerts',
    label: 'Оповещения',
    to: '/ops/alerts',
    icon: BellRing,
    group: 'support',
    operations: sections.alerts.operations,
  },
  {
    id: 'backups',
    label: 'Копии',
    to: '/ops/backups',
    icon: DatabaseBackup,
    group: 'support',
    operations: sections.backups.operations,
  },
];

/**
 * Пункт меню подсвечен и на экранах вне своего адреса: открытый протокол живёт в «Документах»
 * (путь назад к папкам — крошки шапки), хотя адрес у него свой — /protocols/…
 */
const navAlsoActive: Readonly<Record<string, readonly string[]>> = {
  '/documents': ['/protocols/'],
};

/** Экран по адресу pathname — часть раздела пункта, хотя адрес не совпадает с адресом пункта. */
export function navActiveElsewhere(to: string, pathname: string): boolean {
  return (navAlsoActive[to] ?? []).some((prefix) => pathname.startsWith(prefix));
}

/**
 * Группа служебных экранов у сопровождения (002, contracts/ui.md, «Меню»). Группы «Сервис»
 * у администратора клиента с 0.22.2 нет: его «Состояние» и «Выкатки» — в «Настройках».
 */
export const SUPPORT_GROUP_TITLE = 'Сопровождение';

/** Кто вошёл — сверх прав (030): связь с человеком справочника, сеанс по ссылке, роли. */
export type NavSession = Pick<MeResponse, 'person' | 'linkSession' | 'roles' | 'external' | 'team'>;

/**
 * «Главная» пользователя — «Мои задачи» (030: FR-031, FR-034; ASG-DESK-07): у сеанса по ссылке
 * и у связанного с человеком сотрудника без ролей установки. Внешнему без ролей «Главная» —
 * «Открытые вам протоколы» (022): свои пункты у него — пунктом меню.
 */
export function homeIsMyItems(me: NavSession): boolean {
  if (me.person === null) return false;
  return me.linkSession || (me.roles.length === 0 && !me.external);
}

/**
 * Разрешённые пункты меню в порядке navItems. session — кто вошёл (030): «Мои задачи» — по связи
 * с человеком; сеансу по ссылке — только они, «Входящие» и руководителю «Просрочено у ваших
 * сотрудников» (032); «Главная» — «Мои задачи», когда homeIsMyItems, и тогда «Входящие» — после них,
 * как после «Главной».
 * Без session — только права (палитра и поиск без данных сеанса).
 */
export function visibleNavItems(permissions: Permissions, session?: NavSession): NavItem[] {
  const person = session?.person != null;
  const myHome = session ? session.linkSession || homeIsMyItems(session) : false;
  // 032: руководитель — и в узком меню сеанса по ссылке.
  const team = person && session?.team === true;
  const items = navItems.filter((item) => {
    if (item.id === 'my') return person;
    if (item.id === 'inbox') return true;
    if (item.id === 'team') return team;
    if (session?.linkSession) return false;
    if (item.id === 'home' && myHome) return false;
    return can(permissions, item.operations);
  });
  if (!myHome || !person) return items;
  // «Мои задачи» на месте «Главной»: первыми, «Входящие» — за ними.
  return [...items.filter((item) => item.id === 'my'), ...items.filter((item) => item.id !== 'my')];
}

/**
 * Откуда число у пункта меню: действующие оповещения (002), «К разбору» справочника (005), свои
 * пункты «просрочено и сегодня» (030, my.summary), непрочитанные «требует действия» ленты
 * уведомлений (032, actionUnread).
 */
export type NavCounterSource = 'activeAlerts' | 'triage' | 'mine' | 'inbox';

/**
 * 0.26.1 (ACC-06): что считает число «Входящих» и колокольчика — для программ чтения и подсказки:
 * «Входящие, непрочитанные, требуют действия: 3».
 */
export const INBOX_COUNTER_SPOKEN = 'непрочитанные, требуют действия';

export interface NavCounter {
  source: NavCounterSource;
  /** Что считает число — для программ чтения экрана: «Оповещения, действующих: 3». */
  spoken: string;
  /** Акцентный счётчик — один на меню (README «Табеля»): у «Входящих»; остальные серые. */
  accent: boolean;
}

/**
 * Счётчики у пунктов меню (contracts/ui.md 002, «Меню»; 005, макет 5): только то, что требует
 * действия, ноль не показывается. «Входящие» (032) — непрочитанные «требует действия», акцентный,
 * единственный такой (README «Табеля»); «Оповещения» — число действующих, серый; «Справочник» —
 * «К разбору» (directory.triage, total), серый; «Мои задачи» (030) — просрочено и на сегодня
 * (my.summary, overdue + today), серый.
 */
export const navCounters: Readonly<Record<string, NavCounter>> = {
  // 032 (FR-003, SC-005; ASG-HIST-04): тот же опрос, что у колокольчика (notificationsQuery).
  // 0.26.1 (ACC-06): смысл прежний — непрочитанные «требует действия», подпись однозначная:
  // прочитанные, но не разобранные — в «Нерешённых» «Входящих», не в этом числе.
  [INBOX_PATH]: {
    source: 'inbox',
    spoken: INBOX_COUNTER_SPOKEN,
    accent: true,
  },
  '/ops/alerts': { source: 'activeAlerts', spoken: 'действующих', accent: false },
  '/directory': { source: 'triage', spoken: 'к разбору', accent: false },
  // 030 (FR-033; ASG-DESK-08): просрочено и на сегодня, серый.
  [MY_ITEMS_PATH]: { source: 'mine', spoken: 'просрочено и на сегодня', accent: false },
};

// --- «Создать ▾» ---

/**
 * Окна создания: пункты «Создать ▾» и окно «Выдать доступ сопровождению» (его открывают
 * палитра и раздел «Доступ сопровождения», в «Создать ▾» его нет — contracts/ui.md).
 * Следующие спецификации добавляют свои.
 */
export type CreateKind =
  'document' | 'maintainedProtocol' | 'user' | 'substitution' | 'supportGrant';

export interface CreateItem {
  kind: CreateKind;
  label: string;
  icon: LucideIcon;
  operations: readonly Operation[];
  /** Без этих прав окно не заполнить, даже если операция разрешена. */
  requires?: readonly Operation[];
}

export const createItems: readonly CreateItem[] = [
  // 003, приёмка 0.3.1: «Новый протокол» — окно загрузки на странице «Документы».
  { kind: 'document', label: 'Протокол', icon: FileUp, operations: ['documents.create'] },
  // 014 (T029; FR-032): протокол без файла — окно «Протокол в системе»; ведущего выбирают из
  // кандидатов minutes.leads.candidates — тем же правом documents.create.
  {
    kind: 'maintainedProtocol',
    label: 'Протокол в системе',
    icon: FilePlus,
    operations: ['documents.create'],
  },
  { kind: 'user', label: 'Пользователь', icon: UserPlus, operations: ['users.create'] },
  {
    kind: 'substitution',
    label: 'Замещение',
    icon: Repeat2,
    operations: ['substitutions.create'],
    // Заместителя выбирают из кандидатов (GET /api/substitutions/candidates, FR-018б): список
    // открыт администратору клиента и владельцу своей роли, которую можно передать. Роль,
    // полученная по замещению, этого права не даёт — заместителю пункт не показывается.
    requires: ['substitutions.candidates'],
  },
];

/** Окно создания разрешено ролям: операция и всё, без чего окно не заполнить. */
export function canCreate(permissions: Permissions, item: CreateItem): boolean {
  return (
    can(permissions, item.operations) &&
    (item.requires ?? []).every((op) => permissions[op] !== undefined)
  );
}

/**
 * Пункты «Создать ▾»: разрешённые ролям и с готовым окном создания. Пусто — кнопки нет
 * (FR-038а).
 */
export function visibleCreateItems(
  permissions: Permissions,
  available: ReadonlySet<CreateKind>,
): CreateItem[] {
  return createItems.filter((item) => available.has(item.kind) && canCreate(permissions, item));
}

/**
 * Протоколы и пункты читаются без временного доступа. Сопровождению они открываются только по
 * нему (правило grant): без доступа сервер отвечает ACC-003, поэтому Главная и палитра их
 * не запрашивают — свои разделы сопровождение открывает из меню.
 */
export function readsDocuments(permissions: Permissions): boolean {
  const rule = permissions['documents.read'];
  return rule !== undefined && rule !== 'grant';
}

export function readsControl(permissions: Permissions): boolean {
  const rule = permissions['control.read'];
  return rule !== undefined && rule !== 'grant';
}

/** Справочник (005) — как протоколы: сопровождению только по временному доступу. */
export function readsDirectory(permissions: Permissions): boolean {
  const rule = permissions['directory.read'];
  return rule !== undefined && rule !== 'grant';
}

/**
 * Где ищут палитра ⌘K и страница поиска (T137; contracts/ui.md, «Палитра ⌘K»): источник —
 * и право на него. Записи ищутся теми же запросами, что у разделов (features/search/sources.ts).
 */
export const searchSourceOperations = {
  users: ['users.read'],
  substitutions: ['substitutions.read'],
  support: ['support.read'],
  audit: ['audit.read.content', 'audit.read.access'],
} as const satisfies Record<string, readonly Operation[]>;

/**
 * Есть что искать в палитре: режим «Поиск» (Tab) и «Все результаты по «…»» (ux.md, раздел 8).
 * Свои замещения (substitutions.read) — правило «в сеансе» (FR-011а): искать есть что у любого
 * вошедшего, даже без ролей; что именно — по правам ролей. Протоколы по названию и П-N, пункты
 * по тексту (003, приёмка 0.3.1), люди и организации справочника (005, FR-034в) — без
 * временного доступа.
 */
export function canPaletteSearch(permissions: Permissions): boolean {
  return (
    Object.values(searchSourceOperations).some((operations) => can(permissions, operations)) ||
    readsDocuments(permissions) ||
    readsControl(permissions) ||
    readsDirectory(permissions)
  );
}

/**
 * «Выдать доступ сопровождению»: кому — выбирают из пользователей с ролью «сопровождение»
 * (GET /api/users), поэтому нужно и право на список пользователей.
 */
export function canGrantSupport(permissions: Permissions): boolean {
  return can(permissions, ['support.grant']) && permissions['users.read'] !== undefined;
}

// --- Отказ ---

export type ForbiddenCode = Extract<ErrorCode, 'ACC-001' | 'ACC-002' | 'ACC-003'>;

export function isForbiddenCode(value: unknown): value is ForbiddenCode {
  return value === 'ACC-001' || value === 'ACC-002' || value === 'ACC-003';
}

/**
 * «Нет доступа · Нет доступа к этому действию. Обратитесь к администратору клиента: …»;
 * за временным доступом (ACC-003) — тоже к администраторам по ФИО (FR-016).
 */
export function forbiddenView(
  code: ForbiddenCode,
  admins: readonly string[],
): { title: string; text: string; code: ForbiddenCode } {
  const rendered = renderError(code, { admins });
  return { title: 'Нет доступа', text: `${rendered.message}. ${rendered.nextStep}`, code };
}

/**
 * Защита маршрута раздела (beforeLoad). По данным сеанса право есть — раздел открывается.
 * Нет — запрос к серверу: отказ ведёт на «/403» с кодом сервера и пишется в журнал;
 * если права уже выданы (данные сеанса устарели), сеанс перечитывается и раздел открывается.
 */
export function requireSection(section: SectionId) {
  return async ({
    context,
    location,
  }: {
    context: { queryClient: QueryClient };
    location: ParsedLocation;
  }): Promise<void> => {
    const me = await context.queryClient.ensureQueryData(meQuery());
    const { operations, probe } = sections[section];
    if (can(me.permissions, operations)) return;
    try {
      await probe();
    } catch (error) {
      if (isApiError(error) && isForbiddenCode(error.code)) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- так TanStack Router перенаправляет
        throw redirect({
          to: '/403',
          search: {
            code: error.code,
            from: location.pathname,
            ...(error.operation ? { operation: error.operation } : {}),
          },
        });
      }
      throw error;
    }
    await context.queryClient.invalidateQueries({ queryKey: meQueryKey });
  };
}
